/**
 * Access configurator — category picker (step 1).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccessCategoryDef, AccessCategoryId } from "@shared/access-configurator";
import { accessCategoryThumbPath, accessCategoryThumbSvgPath } from "@shared/access-configurator";

interface CategoryPickerProps {
  categories: AccessCategoryDef[];
  onSelect: (id: AccessCategoryId) => void;
}

function CategoryThumb({ id }: { id: AccessCategoryId }) {
  const [src, setSrc] = useState(() => accessCategoryThumbPath(id));
  return (
    <img
      src={src}
      alt=""
      className="w-full h-full object-contain"
      onError={() => {
        const svg = accessCategoryThumbSvgPath(id);
        if (src !== svg) setSrc(svg);
      }}
    />
  );
}

export default function CategoryPicker({ categories, onSelect }: CategoryPickerProps) {
  const { t } = useTranslation();

  return (
    <div className="access-configurator-layout configurator-layout h-full min-h-0 w-full overflow-y-auto bg-background">
      <div className="w-full px-6 py-8 sm:px-10 lg:px-12">
        <header className="mb-8 w-full max-w-none">
          <div className="flex items-center gap-3 mb-[11px]">
            <span className="configurator-deco-line" />
            <p
              className="text-[15px] uppercase tracking-normal"
              style={{
                fontFamily: "Roboto, Arial, Helvetica, sans-serif",
                color: "var(--configurator-form-text)",
              }}
            >
              {t("accessConfigurator.subtitle")}
            </p>
          </div>
          <h1
            style={{
              fontSize: "clamp(2rem, 5vw, 48px)",
              fontFamily: "'Saira Condensed', 'Sarabun', Arial, Helvetica, sans-serif",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            <span className="block" style={{ color: "var(--configurator-form-text)" }}>
              {t("accessConfigurator.titleLine1")}
            </span>
            <span className="block" style={{ color: "var(--configurator-warm-accent)" }}>
              {t("accessConfigurator.titleLine2")}
            </span>
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--configurator-form-text)", opacity: 0.72 }}>
            {t("accessConfigurator.pickCategory")}
          </p>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 w-full">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => onSelect(cat.id)}
              className="group flex flex-col items-stretch rounded-lg border border-border bg-card overflow-hidden text-left transition hover:border-[hsl(var(--configurator-accent))] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--configurator-accent))]"
            >
              <div className="aspect-[4/3] bg-white flex items-center justify-center p-4">
                <CategoryThumb id={cat.id} />
              </div>
              <div className="px-3 py-2.5 border-t border-border">
                <span
                  className="text-sm font-semibold group-hover:text-[hsl(var(--configurator-accent))]"
                  style={{ color: "var(--configurator-form-text)" }}
                >
                  {t(`accessConfigurator.categories.${cat.id}`)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
